import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";

import {
  fmtMoney,
  fmtPct,
  fmtQty,
  fmtRate,
  monthLabel,
  useCosLineItems,
  type LineItemRead,
  type LineOutputRead,
} from "../api";

const PAGE = 25;

/**
 * The invoice lines behind one product line's monthly number. Quick view shows
 * the essentials; the expand chevron animates to full width for the whole
 * mapped detail (vendor, rule, recognized amount).
 */
export function LineItemsDrawer({
  period,
  line,
  open,
  onOpenChange,
  expanded,
  onExpandedChange,
}: {
  period: string;
  line: LineOutputRead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const [offset, setOffset] = useState(0);
  const items = useCosLineItems(
    period,
    { line: line?.product_line_key ?? "", limit: expanded ? 100 : PAGE, offset },
    open && line != null,
  );

  const quickColumns: DataTableColumn<LineItemRead>[] = [
    {
      key: "description",
      header: "Invoice line",
      render: (r) => (
        <div className="max-w-[220px]">
          <div className="truncate font-medium" title={r.description ?? undefined}>
            {r.description ?? "—"}
          </div>
          <div className="truncate text-xs text-muted-foreground">{r.source_sheet ?? "—"}</div>
        </div>
      ),
    },
    {
      key: "monthly",
      header: "Recognized",
      align: "right",
      numeric: true,
      render: (r) => fmtMoney(r.monthly_cogs_amount ?? r.raw_amount),
    },
  ];

  const expandedColumns: DataTableColumn<LineItemRead>[] = [
    {
      key: "description",
      header: "Invoice line",
      render: (r) => (
        <div className="max-w-[380px]">
          <div className="truncate font-medium" title={r.description ?? undefined}>
            {r.description ?? "—"}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {r.upload_filename ?? "—"}
          </div>
        </div>
      ),
    },
    { key: "sheet", header: "Sheet", render: (r) => r.source_sheet ?? "—" },
    {
      key: "vendor",
      header: "Vendor / rule",
      render: (r) => (
        <div>
          <div>{r.vendor_name ?? "—"}</div>
          <div className="text-xs text-muted-foreground">{r.charge_type_name ?? "—"}</div>
        </div>
      ),
    },
    {
      key: "raw",
      header: "Invoice amount",
      align: "right",
      numeric: true,
      render: (r) => fmtMoney(r.raw_amount),
    },
    {
      key: "monthly",
      header: "Recognized this month",
      align: "right",
      numeric: true,
      render: (r) => fmtMoney(r.monthly_cogs_amount),
    },
    {
      key: "qty",
      header: "Qty",
      align: "right",
      numeric: true,
      render: (r) => fmtQty(r.qty),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge value={r.status} />,
    },
  ];

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={line?.title ?? ""}
      subtitle={`${monthLabel(period)} · ${line?.metric_label ?? ""}`}
    >
      {line != null && (
        <div className={cn("flex flex-col gap-4", expanded ? "p-5" : "p-4")}>
          <div className={cn("grid gap-3", expanded ? "grid-cols-4" : "grid-cols-2")}>
            <KpiTile label="Total expense" value={fmtMoney(line.total_expense)} />
            <KpiTile
              label={line.metric_label}
              value={fmtQty(line.metric_units)}
              sub={line.metric_source ?? undefined}
            />
            <KpiTile label="$/Metric" value={fmtRate(line.dollar_per_metric_actual)} />
            {expanded ? (
              <KpiTile
                label="Full cap excl / incl tax"
                value={
                  <span className="text-base">
                    {fmtRate(line.full_cap_per_metric_excl)} /{" "}
                    {fmtRate(line.full_cap_per_metric_incl)}
                  </span>
                }
                sub={line.utilization != null ? `${fmtPct(line.utilization)} utilized` : undefined}
              />
            ) : (
              <KpiTile
                label="Utilization"
                value={line.utilization != null ? fmtPct(line.utilization) : "—"}
              />
            )}
          </div>

          {line.workbook_total != null && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">tie-out</Badge>
              Workbook Simple Summary said {fmtMoney(line.workbook_total)} for this line
              {line.total_expense != null &&
                ` — ${
                  Math.abs(line.workbook_total - line.total_expense) <= 0.5
                    ? "matches the computed total."
                    : `computed ${fmtMoney(line.total_expense)} (Δ ${fmtMoney(
                        line.total_expense - line.workbook_total,
                      )}).`
                }`}
            </div>
          )}

          <DataTable
            columns={expanded ? expandedColumns : quickColumns}
            rows={items.data?.items ?? []}
            rowKey={(r) => r.id}
            loading={items.isLoading}
            emptyMessage="No invoice lines mapped to this product line yet."
            pagination={
              items.data && items.data.count > (expanded ? 100 : PAGE)
                ? {
                    limit: items.data.limit,
                    offset: items.data.offset,
                    count: items.data.count,
                  }
                : undefined
            }
            onPageChange={setOffset}
          />
        </div>
      )}
    </EntityDrawer>
  );
}
